import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Avatar,
  ThemeProvider,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';
import { theme } from '../../../theme';

const OrderDetails: React.FC<{ order: any }> = ({ order }) => {
  console.log('OrderDetails received order:', order);
  
  if (!order) {
    return (
      <Box p={3} textAlign="center">
        <Typography variant="h6" color="textSecondary">
          No order data available
        </Typography>
      </Box>
    );
  }

  // Access the correct data structure based on API response
  const billing = order?.invoiceDetails?.billingTo;
  console.log('Billing Address:', billing);
  const shipping = order?.shippingAddress || order?.deliveryAddress;
  console.log('Shipping Address:', shipping);
  const items = order?.orderItems || order?.items || [];

  console.log('Order Items:', items);
  
  console.log('Extracted data:', { billing, shipping, items, orderKeys: Object.keys(order) });

  const subtotal = order?.invoiceDetails?.subTotal || 0;
  const shippingRate = order?.invoiceDetails?.deliveryFee || order?.shippingRate || 0;
  const vat = order?.invoiceDetails?.tax || 0;
  const grandTotal = order?.invoiceDetails?.total || order?.orderAmount || (subtotal + shippingRate + vat);

  return (
    <ThemeProvider theme={theme}>
      <Box p={3}>
        {/* Billing and Shipping Address */}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mb: 2 }}>
          <Card variant="outlined">
            <CardContent>
              <Typography fontWeight={600} mb={1}>
                Billing Address
              </Typography>
              <Typography sx={{ whiteSpace: 'pre-line' }}>
                {shipping ? [
                  shipping.firstName,
                  shipping.phoneNumber,
                  shipping.addressLine,
                  
                ]
                  .filter(Boolean)
                  .join('\n') : 'No billing address available'}
              </Typography>
            </CardContent>
          </Card>
          <Card variant="outlined">
            <CardContent>
              <Typography fontWeight={600} mb={1}>
                Shipping Address
              </Typography>
              <Typography sx={{ whiteSpace: 'pre-line' }}>
                {shipping ? [
                  shipping.firstName,
                  shipping.phoneNumber,
                  shipping.addressLine,
                  shipping.state,
                  shipping.postalCode,
                  shipping.country,
                ]
                  .filter(Boolean)
                  .join('\n') : 'No shipping address available'}
              </Typography>
            </CardContent>
          </Card>
        </Box>

        {/* Order ID and Info */}
        <Typography variant="h6" fontWeight={600} mb={2}>
          Order ID : {order.orderNumber || order.orderId}
        </Typography>

        {/* Product Table */}
        <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                <TableCell>Image</TableCell>
                <TableCell>Product</TableCell>
                {/* <TableCell>SKU</TableCell> */}
                <TableCell align="center">Qty</TableCell>
                <TableCell align="right">Unit Price</TableCell>
                <TableCell align="right">Total</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.length > 0 ? (
                items.map((item: any, index: number) => (
                  <TableRow key={item.itemId || index}>
                    <TableCell>
                      <Avatar
                        src={item.imageUrl}
                        alt={item.itemName}
                        sx={{ width: 48, height: 48 }}
                        variant="square"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography>{item.itemName}</Typography>
                      {/* Display choice names */}
                      {item.choices && item.choices.length > 0 && (
                        <Box sx={{ mt: 0.5 }}>
                          {item.choices.map((choice: any, choiceIndex: number) => (
                            <Typography 
                              key={choiceIndex}
                              variant="caption" 
                              color="primary" 
                              sx={{ 
                                display: 'block',
                                fontStyle: 'italic',
                                fontSize: '0.75rem'
                              }}
                            >
                              + {choice.choiceName} {choice.choicePrice > 0 && `(JOD${choice.choicePrice})`}
                            </Typography>
                          ))}
                        </Box>
                      )}
                      {/* Display meal information */}
                      {item.mealId && item.mealName && (
                        <Box sx={{ mt: 1, p: 1, backgroundColor: '#f0f7ff', borderRadius: 1 }}>
                          <Typography 
                            variant="caption" 
                            color="secondary" 
                            sx={{ 
                              display: 'block',
                              fontWeight: 600,
                              fontSize: '0.8rem',
                              mb: 0.5
                            }}
                          >
                            🍽️ Meal: {item.mealName} {item.mealPrice > 0 && `(JOD${item.mealPrice.toFixed(2)})`}
                          </Typography>
                          {item.mealChoiceGroups && item.mealChoiceGroups.length > 0 && (
                            <Box sx={{ pl: 1 }}>
                              {item.mealChoiceGroups.map((group: any, groupIndex: number) => (
                                <Box key={groupIndex} sx={{ mb: 0.5 }}>
                                  <Typography 
                                    variant="caption" 
                                    sx={{ 
                                      display: 'block',
                                      fontSize: '0.7rem',
                                      color: 'text.secondary'
                                    }}
                                  >
                                    {group.choiceGroupName}:
                                  </Typography>
                                  {group.choices && group.choices.map((choice: any, choiceIndex: number) => (
                                    <Typography 
                                      key={choiceIndex}
                                      variant="caption" 
                                      sx={{ 
                                        display: 'block',
                                        fontSize: '0.7rem',
                                        pl: 1,
                                        color: 'text.secondary'
                                      }}
                                    >
                                      • {choice.choiceName} {choice.choicePrice > 0 && `(+JOD${choice.choicePrice.toFixed(2)})`}
                                    </Typography>
                                  ))}
                                </Box>
                              ))}
                            </Box>
                          )}
                        </Box>
                      )}
                    </TableCell>
                    {/* <TableCell>
                      <Typography>{item.itemCode || item.sku || 'N/A'}</Typography>
                    </TableCell> */}
                    <TableCell align="center">
                      <Typography>{item.quantity}</Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography>
                        JOD{(() => {
                          // Prioritize discount prices over original price
                          const discountedPriceRaw = item?.discountedPrice ?? item?.discountPrice ?? null;
                          const discountedPrice = discountedPriceRaw != null ? Number(discountedPriceRaw) : null;
                          const originalPrice = item?.originalPrice != null ? Number(item.originalPrice) : 0;
                          const unitPrice = (discountedPrice != null && discountedPrice > 0) ? discountedPrice : originalPrice;
                          return typeof unitPrice === 'number' ? unitPrice.toFixed(2) : '0.00';
                        })()}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography fontWeight={600}>
                        JOD{(() => {
                          const quantity = item.quantity || 0;
                          // Prioritize discount prices over original price
                          const discountedPriceRaw = item?.discountedPrice ?? item?.discountPrice ?? null;
                          const discountedPrice = discountedPriceRaw != null ? Number(discountedPriceRaw) : null;
                          const originalPrice = item?.originalPrice != null ? Number(item.originalPrice) : 0;
                          const unitPrice = (discountedPrice != null && discountedPrice > 0) ? discountedPrice : originalPrice;
                          const total = quantity * unitPrice;
                          return total > 0 ? total.toFixed(2) : '0.00';
                        })()}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Typography color="textSecondary">
                      No items found in this order
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Order Summary */}
        <Box mt={3} px={2} textAlign="right">
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Box sx={{ width: { xs: '100%', sm: '50%', md: '33.33%' } }}>
              <Box display="flex" justifyContent="space-between" py={0.5}>
                <Typography>Subtotal</Typography>
                <Typography>JOD{subtotal.toFixed(2)}</Typography>
              </Box>
              <Box display="flex" justifyContent="space-between" py={0.5}>
                <Typography>VAT ({vat > 0 ? '5%' : '0%'})</Typography>
                <Typography>JOD{vat.toFixed(2)}</Typography>
              </Box>
              {shippingRate > 0 && (
                <Box display="flex" justifyContent="space-between" py={0.5}>
                  <Typography>Delivery Fee</Typography>
                  <Typography>JOD{shippingRate.toFixed(2)}</Typography>
                </Box>
              )}
              <Divider sx={{ my: 1 }} />
              <Box display="flex" justifyContent="space-between">
                <Typography fontWeight={600}>Grand Total</Typography>
                <Typography fontWeight={600} fontSize="1.1rem">
                  JOD{grandTotal.toFixed(2)}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </ThemeProvider>
  );
};

export default OrderDetails; 